import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { useUrlFilterState } from '../../lib/url-filter-state';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { ReportAnalyticsGrid, ReportAreaChartCard, ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { lastMonthDateFilters } from './report-filters';
import { fetchDeathReport } from './reports.api';
import { useDeathReport } from './reports.queries';
import type { DischargeReportRow, ReportListFilters } from './types';

const initialFilters = (): ReportListFilters => ({ page: 1, limit: 10, ...lastMonthDateFilters(), value: '' });
const initialDates = lastMonthDateFilters;
const SEARCH_DEBOUNCE_MS = 350;

const upper = (value: string | null | undefined) => value?.trim() ? value.trim().toUpperCase() : '-';
// Chart axis uses the bare doctor name; the list and export prefix it with "DR.".
const doctorText = (value: string) => upper(value.replace(/^\s*(dr\.?\s+)+/i, ''));
const doctorListText = (value: string) => {
  const name = doctorText(value);
  return name === '-' ? '-' : `DR. ${name}`;
};
const patientText = (row: Pick<DischargeReportRow, 'patientName' | 'uhid'>) =>
  row.uhid ? `${row.patientName} (${row.uhid})` : row.patientName;
const dateText = (value: string | null) => value ? formatIpdDateTime(value) : '-';

export const DeathReportPage = () => {
  const [filters, setFilters] = useUrlFilterState<ReportListFilters>(initialFilters);
  const [dates, setDates] = useState(() => ({ fromDate: filters.fromDate ?? '', toDate: filters.toDate ?? '' }));
  const [search, setSearch] = useState(filters.value ?? '');
  const query = useDeathReport(filters);
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });

  // Table search runs server-side so it covers every page, not just the rows on screen.
  useEffect(() => {
    const term = search.trim();
    const timer = window.setTimeout(() => {
      setFilters((current) => ((current.value ?? '') === term ? current : { ...current, value: term, page: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search, setFilters]);

  const applyDates = (event: FormEvent) => {
    event.preventDefault();
    setFilters((current) => ({ ...current, ...dates, page: 1 }));
  };
  const reset = () => {
    setDates(initialDates());
    setSearch('');
    setFilters(initialFilters());
  };
  const changeLimit = (limit: number) => setFilters((current) => ({ ...current, limit, page: 1 }));

  const rows = query.data?.data ?? [];
  const summary = query.data?.summary;
  const pagination = query.data?.pagination;
  const firstRowNumber = pagination ? (pagination.page - 1) * pagination.limit + 1 : 1;
  const genderChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'MALE', value: summary?.gender.male ?? 0, color: '#1a8cf0' },
    { name: 'FEMALE', value: summary?.gender.female ?? 0, color: '#0a9f6c' },
    { name: 'OTHERS', value: summary?.gender.others ?? 0, color: '#c98a1c' },
  ], [summary?.gender.female, summary?.gender.male, summary?.gender.others]);
  const doctorChartData = useMemo<ReportChartDatum[]>(() => (summary?.byDoctor ?? [])
    .map((item) => ({ name: doctorText(item.doctor), Total: item.count }))
    .sort((left, right) => left.name.localeCompare(right.name)), [summary?.byDoctor]);
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Death report could not be loaded.';

  const exportToExcel = async () => {
    const exportRows = await fetchAllPages<DischargeReportRow>((page, limit) => fetchDeathReport({ ...filters, page, limit }), 500);
    await exportRowsToExcel({
      fileName: `Death-Report-${new Date().toISOString().slice(0, 10)}`,
      title: 'Death Report',
      subtitle: filters.fromDate && filters.toDate ? `${filters.fromDate} to ${filters.toDate}` : 'All dates',
      columns: [
        { header: 'SN', width: 8, align: 'right', map: (_row, index) => index + 1 },
        { header: 'Patient Name', width: 32, map: (row) => upper(patientText(row)) },
        { header: 'Gender', width: 12, map: (row) => upper(row.gender) },
        { header: 'Age', width: 14, map: (row) => row.ageDisplay },
        { header: 'Admission Date', width: 22, map: (row) => dateText(row.admissionDate) },
        { header: 'Under Doctor', width: 30, map: (row) => doctorListText(row.doctorName) },
        { header: 'Death Date', width: 22, map: (row) => dateText(row.dischargeDate) },
      ],
      rows: exportRows,
    });
  };

  return (
    <div className="page-stack report-list-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>Death Report</h1>
          <p>Death discharge records by patient, doctor, admission date, and death date.</p>
        </div>
      </div>

      <form className="report-list-filter-card death-report-filters" onSubmit={applyDates}>
        <label>
          From date
          <input type="date" value={dates.fromDate} max={dates.toDate || undefined} onChange={(event) => setDates((current) => ({ ...current, fromDate: event.target.value }))} />
        </label>
        <label>
          To date
          <input type="date" value={dates.toDate} min={dates.fromDate || undefined} onChange={(event) => setDates((current) => ({ ...current, toDate: event.target.value }))} />
        </label>
        <button className="button button-primary report-list-action" type="submit"><IconSearch />Apply</button>
        <button className="button button-secondary report-list-action" type="button" onClick={reset}><IconRefreshCw />Reset</button>
        <ExcelExportButton className="report-list-action" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
      </form>

      {query.isSuccess ? (
        <ReportAnalyticsGrid className="death-report-analytics">
          <ReportDonutChartCard eyebrow="Gender analysis" title="Gender distribution" data={genderChartData} variant="pie" height={300} />
          <ReportAreaChartCard
            eyebrow="Doctor analysis"
            title="Total Doctors"
            data={doctorChartData}
            areas={[{ key: 'Total', name: 'Total', color: '#1a8cf0' }]}
            height={300}
            emptyTitle="No doctor data"
          />
        </ReportAnalyticsGrid>
      ) : null}

      <section className="content-card report-list-card">
        <div className="table-toolbar">
          <label className="billing-limit-select">
            Show
            <select value={filters.limit} disabled={query.isFetching} onChange={(event) => changeLimit(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
          <label className="report-table-search">
            Search:
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Patient, UHID, doctor" />
          </label>
        </div>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load Death Report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && rows.length === 0 ? <EmptyState title="No death records found" message="Change the search, filters, or date range and try again." /> : null}
        {query.isSuccess && rows.length > 0 ? (
          <>
            <div className="report-table-scroll">
              <table className="report-data-table death-report-table">
                <thead>
                  <tr>
                    <th>SN</th>
                    <SortableTh control={tableSort} column="patient">Patient Name</SortableTh>
                    <SortableTh control={tableSort} column="gender">Gender</SortableTh>
                    <th>Age</th>
                    <SortableTh control={tableSort} column="admissionDate">Admission Date</SortableTh>
                    <SortableTh control={tableSort} column="doctor">Under Doctor</SortableTh>
                    <SortableTh control={tableSort} column="dischargeDate">Death Date</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{firstRowNumber + index}</td>
                      <td>
                        {row.patientId
                          ? <Link className="patient-name-link" to={`/patients/${row.patientId}`} title="Open patient profile">{patientText(row)}</Link>
                          : patientText(row)}
                      </td>
                      <td>{upper(row.gender)}</td>
                      <td>{row.ageDisplay}</td>
                      <td>{dateText(row.admissionDate)}</td>
                      <td>{doctorListText(row.doctorName)}</td>
                      <td>{dateText(row.dischargeDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <span className="pagination-summary">
                Showing {firstRowNumber.toLocaleString('en-IN')} to {(firstRowNumber + rows.length - 1).toLocaleString('en-IN')} of {query.data.pagination.total.toLocaleString('en-IN')} entries
              </span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};
